import { scheduleStageText } from "./paymentSchedule";

function formatReceiptDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const pad = (part) => String(part).padStart(2, "0");
  const hours = date.getHours();
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(hours % 12 || 12)}:${pad(date.getMinutes())} ${hours >= 12 ? "PM" : "AM"}`;
}

// tripStartDate/tripEndDate include stay-only extra nights. When one of those reaches past the
// vehicle pickup/drop, that end shows the stay date alone - there is no vehicle time for it.
function receiptTravelDates(data) {
  const pickupDate = String(data.pickupDateTime || "").slice(0, 10);
  const dropDate = String(data.dropDateTime || "").slice(0, 10);
  const start = data.tripStartDate && pickupDate && data.tripStartDate < pickupDate
    ? formatReceiptDate(data.tripStartDate)
    : formatReceiptDateTime(data.pickupDateTime);
  const end = data.tripEndDate && dropDate && data.tripEndDate > dropDate
    ? formatReceiptDate(data.tripEndDate)
    : formatReceiptDateTime(data.dropDateTime);
  return `${start} - ${end}`;
}

function formatReceiptDate(isoDate) {
  const [year, month, day] = String(isoDate).slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

async function loadPublicImageBitmap(src) {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    return await createImageBitmap(await response.blob());
  } catch {
    return null;
  }
}

function wrapReceiptTextLines(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function drawWrappedReceiptText(ctx, text, x, y, maxWidth, lineHeight) {
  const lines = wrapReceiptTextLines(ctx, text, maxWidth);
  lines.forEach((line, index) => ctx.fillText(line, x, y + index * lineHeight));
  return lines.length;
}

function drawLines(ctx, lines, x, y, lineHeight) {
  lines.forEach((line, index) => ctx.fillText(line, x, y + index * lineHeight));
}

export async function generatePaymentReceipt(data) {
  const rows = [
    ["Customer Name / Contact Number", `${data.customerName || "-"} / ${data.customerNumber || "-"}`],
    ["Pickup / Drop", data.pickup || "-"],
    ["Travel Dates", receiptTravelDates(data)],
    ["Package Details", `${data.adults || 0} Adults, ${data.kids || 0} Kids${data.kids && data.kidsAge ? ` (age ${data.kidsAge})` : ""} - ${data.trip || "-"}`],
    ["Package Duration / Vehicle", `${data.duration || "-"}, ${data.vehicle || "-"}`],
    // Array value: each night is drawn on its own line rather than one long joined string.
    ["Hotel / Room Details", (data.rooms || []).length ? (data.rooms || []) : "-"],
    ["Add-ons", data.addons?.length ? data.addons.join(", ") : "None"],
    ["Customization", data.customization || "No"],
    ["Notes", data.bookingNotes || "No"],
    ["Total Package Amount", `INR ${Number(data.packageTotal || 0).toLocaleString("en-IN")}`],
    ["Advance Paid", `INR ${Number(data.advancePaid || 0).toLocaleString("en-IN")}`],
    ["Balance Remaining", `INR ${Number(data.balance || 0).toLocaleString("en-IN")}`],
    // Bookings on the two-stage terms show how the balance is paid; older ones have no schedule.
    ...(data.paymentSchedule
      ? [
          ["Pay After Boarding (25%)", scheduleStageText(data.paymentSchedule.afterBoardingAmount, data.paymentSchedule.afterBoardingDue)],
          ["Pay on Day 1 after Check-in", scheduleStageText(data.paymentSchedule.dayOneAmount, data.paymentSchedule.dayOneDue)],
        ]
      : []),
    ...(Number(data.totalCashbackAmount || 0) > 0
      ? [
          ["Cashback Amount Per Adult", `INR ${Number(data.cashbackAmountPerPerson || 0).toLocaleString("en-IN")}`],
          ["Total Cashback", `INR ${Number(data.totalCashbackAmount || 0).toLocaleString("en-IN")}`],
        ]
      : []),
  ];

  // Row height has to be measured with the same font and width the value is drawn at. Estimating
  // it from character count under-counts long hotel lists, and the overflow gets painted over by
  // the next row - which is why long Hotel / Room Details used to look truncated.
  const measureCtx = document.createElement("canvas").getContext("2d");
  const valueEntries = (value) => (Array.isArray(value) ? value : [String(value || "-")]);
  const rowHeights = rows.map(([label, value]) => {
    const isWrapped = label === "Hotel / Room Details" || label === "Add-ons" || label === "Customization" || label === "Notes";
    if (!isWrapped) return 72;
    measureCtx.font = "22px Arial";
    const wrappedLines = valueEntries(value)
      .reduce((sum, entry) => sum + Math.max(1, wrapReceiptTextLines(measureCtx, entry, 600).length), 0);
    return Math.max(105, 45 + wrappedLines * 30);
  });
  const rowsHeight = rowHeights.reduce((sum, height) => sum + height, 0);

  const policyText = "Payment Policy: Pay 25% of the total package amount right after boarding the vehicle, and the remaining balance on Day 1 immediately after hotel check-in, for smooth continuation of all services.";
  measureCtx.font = "600 21px Arial";
  const policyLines = wrapReceiptTextLines(measureCtx, policyText, 1070);

  // Canvas height is sized to fit the actual content (table rows + policy text) instead of a
  // fixed value, so the footer links never get forced to overlap the policy text on long receipts.
  const tableTop = 630;
  const policyBlockHeight = 48 + policyLines.length * 31 + 42;
  const bottomMargin = 100;
  const canvasHeight = Math.max(1900, tableTop + rowsHeight + policyBlockHeight + bottomMargin);

  const canvas = document.createElement("canvas");
  canvas.width = 1240;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = "#d1d5db";
  ctx.lineWidth = 3;
  ctx.strokeRect(55, 40, 1130, canvasHeight - 80);

  ctx.fillStyle = "#20242a";
  ctx.font = "500 54px Arial";
  ctx.fillText("PAYMENT", 105, 115);
  ctx.fillText("RECEIPT", 105, 175);
  const logoBitmap = await loadPublicImageBitmap("/img/icon.png");
  if (logoBitmap) ctx.drawImage(logoBitmap, 970, 62, 145, 125);

  ctx.textAlign = "right";
  ctx.font = "700 34px Georgia";
  ctx.fillStyle = "#172b45";
  ctx.fillText("INDIAN ODYSSEY", 1125, 210);
  ctx.fillText("TRIPS", 1125, 250);
  ctx.textAlign = "left";

  ctx.font = "24px Arial";
  ctx.fillStyle = "#4b5563";
  ctx.fillText("Thank you for choosing Indian Odyssey!", 105, 245);
  ctx.fillText("We truly appreciate your trust in us. Our team is committed to making your trip", 105, 282);
  ctx.fillText("smooth, joyful, and memorable.", 105, 319);

  ctx.font = "700 25px Arial";
  ctx.fillStyle = "#155eaa";
  ctx.fillText("Indian Odyssey", 105, 375);
  ctx.font = "22px Arial";
  ctx.fillStyle = "#374151";
  ctx.fillText("Phone: +91 91219 43442", 105, 414);
  ctx.fillText("Website: www.iotrips.in", 105, 451);

  ctx.fillStyle = "#0759a8";
  ctx.fillRect(85, 535, 1070, 58);
  ctx.fillStyle = "#fff";
  ctx.font = "700 23px Arial";
  ctx.fillText("RECEIPT NO", 120, 572);
  ctx.textAlign = "right";
  ctx.fillText(data.receiptNumber, 1120, 572);
  ctx.textAlign = "left";

  // The shaded row: the last one, except that the payment schedule rows sit below Balance Remaining
  // and the balance keeps the shading.
  const shadedRow = data.paymentSchedule ? rows.findIndex(([label]) => label === "Balance Remaining") : rows.length - 1;
  let y = tableTop;
  rows.forEach(([label, value], index) => {
    const height = rowHeights[index];
    ctx.fillStyle = index === shadedRow ? "#ebe8df" : index % 2 ? "#fafafa" : "#fff";
    ctx.fillRect(85, y, 1070, height);
    ctx.strokeStyle = "#e5e7eb";
    ctx.beginPath();
    ctx.moveTo(85, y + height);
    ctx.lineTo(1155, y + height);
    ctx.stroke();
    ctx.fillStyle = "#4b5563";
    ctx.font = "600 21px Arial";
    ctx.fillText(`${label}:`, 115, y + 43);
    ctx.fillStyle = "#20242a";
    ctx.font = "22px Arial";
    let valueY = y + 43;
    valueEntries(value).forEach((entry) => {
      valueY += drawWrappedReceiptText(ctx, entry, 520, valueY, 600, 30) * 30;
    });
    y += height;
  });

  ctx.fillStyle = "#374151";
  ctx.font = "600 21px Arial";
  drawLines(ctx, policyLines, 85, y + 48, 31);
  const footerY = y + 48 + policyLines.length * 31 + 42;
  ctx.fillStyle = "#0759a8";
  ctx.font = "700 24px Arial";
  ctx.fillText("www.iotrips.in", 100, footerY);
  ctx.textAlign = "center";
  ctx.fillText("@indianodyssey.in", 620, footerY);
  ctx.textAlign = "right";
  ctx.fillText("View on Google", 1140, footerY);
  ctx.textAlign = "left";

  const link = document.createElement("a");
  const safeName = String(data.customerName || "Customer").replace(/[<>:"/\\|?*]/g, "").trim();
  link.download = `Payment Receipt - ${data.receiptNumber} - ${safeName}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
  logoBitmap?.close?.();
}
